import { useRequest } from 'ahooks'
import { Button, Table, message } from 'antd'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { usersAtom } from '@/modules/user'
import { routes } from '@/router'
import { Team } from '@/schemas'

interface RowProps {
  email: string
  period: string
  dateRange: string
  workload: number
}

export const TeamResourceWarningTable = React.forwardRef<
  { download: () => void },
  { departmentId: number; team: Team }
>(({ departmentId, team }, ref) => {
  const { t } = useTranslation()
  const [users] = useAtom(usersAtom)

  const [notification, setNotification] = React.useState<{ email: string; period: string }>()

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const { data } = useRequest(
    {
      url: '/api/work-resource/statistics/team/warning/list',
      params: {
        startDate,
        endDate,
        departmentId,
      },
    },
    {
      ready: !!startDate && !!endDate && !!departmentId,
      refreshDeps: [startDate, endDate, departmentId],
    }
  )

  const { run, loading } = useRequest(
    (data) => ({
      method: 'post',
      url: '/api/work-resource/statistics/team/warning/notification',
      data,
    }),
    {
      manual: true,
      onSuccess(data: { email: string; year: string; week: string }) {
        setNotification(undefined)
        const user = users.find((u) => u.email === data.email)
        message.success(
          `${user?.name || data.email} ${t('dashboard.xAxisLabel', { year: data.year, week: data.week })}通知已成功发送`
        )
      },
      onError() {
        setNotification(undefined)
        message.error('通知发送失败')
      },
    }
  )

  const { run: download } = useRequest(
    (data) => {
      return {
        url: '/api/work-resource/statistics/team/warning/download',
        method: 'post',
        data,
        responseType: 'blob',
      }
    },
    {
      manual: true,
      onSuccess(res: any) {
        saveAs(res, `${departmentId}-${team.name}-资源填写小于5人日列表-${startDate}-${endDate}.xlsx`)
      },
    }
  )

  React.useImperativeHandle(
    ref,
    () => ({
      download: () => {
        download({ departmentId, startDate, endDate })
      },
    }),
    [startDate, endDate, departmentId]
  )

  React.useEffect(() => {
    if (notification) {
      const [year, week] = notification.period.split('-')
      run({ departmentId, email: notification.email, year, week })
    }
  }, [notification])

  const dataSource = data || []

  const columns = [
    {
      title: t('dashboard.table.name'),
      dataIndex: 'name',
      render: (_: any, record: RowProps) => {
        const user = users.find((u) => u.email === record.email)
        return user?.name || record.email
      },
    },
    {
      title: t('period'),
      dataIndex: 'period',
      render: (value: string) => {
        const [year, week] = value.split('-')
        return t('dashboard.xAxisLabel', { year, week })
      },
    },
    {
      title: t('dashboard.table.dateRange'),
      dataIndex: 'dateRange',
      render: (value: string) => {
        const [startDate, endDate] = value.split('_')
        return `${startDate}-${endDate}`
      },
    },
    {
      title: t('dashboard.table.team'),
      render: (_: any, record: RowProps) => {
        const user = users.find((u) => u.email === record.email)
        if (user?.departments.length === 1) {
          const department = user.departments[0]
          return (
            <Link
              to={routes.team.home({ departmentId: department.id })}
              target='__blank'
              onClick={(e) => e.stopPropagation()}
            >
              {department.name}
            </Link>
          )
        }
        return (
          <ul>
            {user?.departments.map((d) => (
              <li key={d.id}>
                <Link
                  to={routes.team.home({ departmentId: d.id })}
                  target='__blank'
                  onClick={(e) => e.stopPropagation()}
                >
                  {d.name}
                </Link>
              </li>
            ))}
          </ul>
        )
      },
    },
    {
      title: t('dashboard.table.workloadCol'),
      dataIndex: 'workload',
      sorter: (a: any, b: any) => Number(a.workload) - Number(b.workload),
      defaultSortOrder: 'ascend' as any,
      render: (value: number) => {
        return value || 0
      },
    },
    {
      title: t('operation'),
      render: (_: any, record: RowProps) => {
        return (
          <Button
            loading={loading && notification?.email === record.email && notification.period === record.period}
            disabled={loading && (notification?.email !== record.email || notification?.period !== record.period)}
            onClick={() => setNotification({ email: record.email, period: record.period })}
            type='link'
          >
            通知
          </Button>
        )
      },
    },
  ]
  return <Table columns={columns} dataSource={dataSource} />
})
